import { PerformanceTableColumnDefinition } from "./PerofmanceTable";

/** Must match backend-supported filter ids (same as QA Postman). */
export const PERFORMANCE_API_FILTERS = [
  "ACCOUNT_VALUE",
  "SURRENDER_VALUE",
  "DEATH_BENEFIT",
  "INCOME",
  "FINANCING",
] as const;

export type ValueMetricId =
  | "accountValue"
  | "surrenderValue"
  | "deathBenefit"
  | "income"
  | "financing"
  | "collateralGap";

export interface MetricBlock {
  projected: number | null;
  actual: number | null;
  trueIQIndex?: number | null;
}

interface IncomeBlock {
  annualIncome: number;
  totalIncome: number;
}

export interface CollateralGapBlock extends MetricBlock {
  policyValue?: number | null;
  requiredCollateral?: number | null;
}

export interface GraphDataRow {
  planYear: number;
  year: number;
  insuredAge: number;
  interestCredits: number | null;
  accountValue: MetricBlock;
  align: MetricBlock;
  surrenderValue: MetricBlock;
  deathBenefit: MetricBlock;
  income: IncomeBlock;
  financing: MetricBlock;
  collateralGap?: CollateralGapBlock;
}

export interface TableDataRow {
  planYear: number;
  year: number;
  insuredAge: number;
  illustratedPlanAccumulatedValue?: number | null;
  illustratedPlanSurrenderValue?: number | null;
  illustratedPlanDB?: number | null;
  estimatedLoanPayoffOrIncome?: number | null;
  totalIncome?: number | null;
  eoyOutstandingLoanBalance?: number | null;
  interestRate?: number | null;
  interestCharge?: number | null;
  lenderPremium?: number | null;
  outsideCollateralRequirement?: number | null;
  actualAccountValue?: number | null;
  actualSurrenderValue?: number | null;
  actualDeathBenefit?: number | null;
  actualFinancingBalance?: number | null;
  actualInterestCredits?: number | null;
  interestCredits?: number | null;
}

export interface PerformanceApiData {
  graphData: GraphDataRow[];
  tableData: TableDataRow[];
}

export const VALUE_METRIC_DEFS: {
  id: ValueMetricId;
  label: string;
  color: string;
}[] = [
  { id: "accountValue", label: "Account Value", color: "#93C5FD" },
  { id: "surrenderValue", label: "Surrender Value", color: "#3B82F6" },
  { id: "deathBenefit", label: "Death Benefit", color: "#22D3EE" },
  { id: "income", label: "Income", color: "#9CA3AF" },
  { id: "financing", label: "Financing", color: "#1E3A8A" },
  { id: "collateralGap", label: "Collateral Gap", color: "#64748B" },
];

export const PRIMARY = "#1E3A8A";

export const fmtMoney = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v)
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(v);

export function getPerformanceTableColumns(): PerformanceTableColumnDefinition[] {
  return [
    { field: "planYear", header: "Plan Yr", style: { minWidth: "80px" } },
    { field: "year", header: "Year", style: { minWidth: "80px" } },
    { field: "insuredAge", header: "Age", style: { minWidth: "70px" } },
    // ── Plan Projections group (illustrated + actual interleaved) ──────────
    {
      field: "illustratedPlanAccumulatedValue",
      header: "Projected Plan Accumulated Value",
      metricId: "accountValue",
      style: { minWidth: "200px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "actualAccountValue",
      header: "Plan Accumulated Value",
      metricId: "accountValue",
      style: { minWidth: "160px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "illustratedPlanSurrenderValue",
      header: "Projected Plan Surrender Value",
      metricId: "surrenderValue",
      style: { minWidth: "190px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "actualSurrenderValue",
      header: "Plan Surrender Value",
      metricId: "surrenderValue",
      style: { minWidth: "180px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "illustratedPlanDB",
      header: "Projected Plan Death Benefit",
      metricId: "deathBenefit",
      style: { minWidth: "180px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "actualDeathBenefit",
      header: "Plan Death Benefit",
      metricId: "deathBenefit",
      style: { minWidth: "160px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "estimatedLoanPayoffOrIncome",
      header: "Estimated Income",
      metricId: "income",
      style: { minWidth: "150px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "totalIncome",
      header: "Total Income",
      metricId: "income",
      style: { minWidth: "140px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    // ── Financing Info group ────────────────────────────────────────────────
    {
      field: "eoyOutstandingLoanBalance",
      header: "EOY Outstanding Financing Balance",
      metricId: "financing",
      style: { minWidth: "220px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "interestRate",
      header: "Interest Rate",
      metricId: "financing",
      style: { minWidth: "120px" },
      formatter: (v: any) => (v == null ? "—" : `${v}%`),
    },
    {
      field: "interestCharge",
      header: "Interest Charge",
      metricId: "financing",
      style: { minWidth: "140px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    // ── Financed Payments group ─────────────────────────────────────────────
    {
      field: "lenderPremium",
      header: "Financed Premium",
      metricId: "financing",
      style: { minWidth: "150px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    // ── (ungrouped) ─────────────────────────────────────────────────────────
    {
      field: "outsideCollateralRequirement",
      header: "Outside Collateral Requirement",
      metricId: "collateralGap",
      style: { minWidth: "200px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    // ── Actuals (financing + interest — no illustrated counterpart) ─────────
    {
      field: "actualFinancingBalance",
      header: "Financing Balance",
      metricId: "financing",
      style: { minWidth: "170px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
    {
      field: "interestCredits",
      header: "Interest Credits",
      style: { minWidth: "140px" },
      formatter: (v: any) => fmtMoney(v as number | null | undefined),
    },
  ];
}

export function toFiniteNumber(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function toTrueIqIndex(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function metricTrueIQIndex(m: MetricBlock | undefined): number | null {
  if (!m || typeof m !== "object") return null;
  const rec = m as unknown as Record<string, unknown>;
  if (rec.trueIQIndex !== undefined) return toTrueIqIndex(rec.trueIQIndex);
  const fuzzyKey = Object.keys(rec).find((k) => k.replace(/\s+/g, "") === "trueIQIndex");
  return fuzzyKey ? toTrueIqIndex(rec[fuzzyKey]) : null;
}

export function pickSeries(
  row: GraphDataRow,
  metricId: ValueMetricId,
  useAlign: boolean,
): { actual: number | null; projected: number | null; trueIQIndex: number | null } {
  if (useAlign && row.align) {
    return {
      actual: toFiniteNumber(row.align.actual),
      projected: toFiniteNumber(row.align.projected),
      trueIQIndex: metricTrueIQIndex(row.align),
    };
  }
  switch (metricId) {
    case "accountValue":
      return {
        actual: toFiniteNumber(row.accountValue.actual),
        projected: toFiniteNumber(row.accountValue.projected),
        trueIQIndex: metricTrueIQIndex(row.accountValue),
      };
    case "surrenderValue":
      return {
        actual: toFiniteNumber(row.surrenderValue.actual),
        projected: toFiniteNumber(row.surrenderValue.projected),
        trueIQIndex: metricTrueIQIndex(row.surrenderValue),
      };
    case "deathBenefit":
      return {
        actual: toFiniteNumber(row.deathBenefit.actual),
        projected: toFiniteNumber(row.deathBenefit.projected),
        trueIQIndex: metricTrueIQIndex(row.deathBenefit),
      };
    case "income":
      return {
        actual: toFiniteNumber(row.income.totalIncome) ?? 0,
        projected: toFiniteNumber(row.income.annualIncome) ?? 0,
        trueIQIndex: null,
      };
    case "financing":
      return {
        actual: toFiniteNumber(row.financing.actual),
        projected: toFiniteNumber(row.financing.projected),
        trueIQIndex: metricTrueIQIndex(row.financing),
      };
    case "collateralGap": {
      const c = row.collateralGap;
      if (!c) return { actual: null, projected: null, trueIQIndex: null };
      return {
        actual: toFiniteNumber(c.actual),
        projected: toFiniteNumber(c.projected),
        trueIQIndex: metricTrueIQIndex(c),
      };
    }
    default:
      return { actual: null, projected: null, trueIQIndex: null };
  }
}

export function metricToChartFilter(metric: ValueMetricId): string {
  if (metric === "deathBenefit") return "deathBenefit";
  if (metric === "income") return "income";
  if (metric === "collateralGap") return "collateralGap";
  return metric;
}

/** Stable order for chart series + TrueIQ primary (first in this list among selected). */
export function orderSelectedMetricsByTabs(
  selected: ValueMetricId[],
  tabs: { id: ValueMetricId }[],
): ValueMetricId[] {
  const set = new Set(selected);
  return tabs.filter((t) => set.has(t.id)).map((t) => t.id);
}
